import React, { useState, useEffect } from "react";
import { Eye, Clock, XCircle, Lock, ShoppingBag, Truck, Package, Calendar, Sun, Moon } from "lucide-react";
import DinnerMoonIcon from "./DinnerMoonIcon";
import LunchSunIcon from "./LunchSunIcon";
import PickupStoreIcon from "./PickupStoreIcon";

// Ticking Analog Watch SVG driven by real time
function TickingWatch() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const seconds = time.getSeconds();
  const minutes = time.getMinutes();
  const hours = time.getHours();

  const secDeg = seconds * 6;
  const minDeg = minutes * 6;
  const hrDeg = (hours % 12) * 30 + minutes * 0.5;

  return (
    <div className="relative w-4 h-4 shrink-0 flex items-center justify-center bg-brand-red-light/20 rounded-full border border-brand-red/20 shadow-3xs">
      <svg className="w-3 h-3 text-brand-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10" />
        {/* Hour hand */}
        <g transform={`rotate(${hrDeg}, 12, 12)`}>
          <line x1="12" y1="12" x2="12" y2="7.5" stroke="currentColor" strokeWidth="2.5" />
        </g>
        {/* Minute hand */}
        <g transform={`rotate(${minDeg}, 12, 12)`}>
          <line x1="12" y1="12" x2="12" y2="5.5" stroke="currentColor" strokeWidth="1.8" />
        </g>
        {/* Second hand */}
        <g transform={`rotate(${secDeg}, 12, 12)`}>
          <line x1="12" y1="12" x2="12" y2="4.5" stroke="#e8423a" strokeWidth="1" />
        </g>
        <circle cx="12" cy="12" r="1" fill="currentColor" />
      </svg>
    </div>
  );
}

// Inline helper component for dynamic per-row cutoff countdown
function RowTimer({ category, lunchCutoffTime, dinnerCutoffTime, orderStatus }) {
  const [timeLeft, setTimeLeft] = useState("");
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    if (orderStatus === "Cancelled" || orderStatus === "Completed" || orderStatus === "Delivered") {
      setTimeLeft("--");
      return;
    }

    const cutoffStr = category?.toLowerCase() === "dinner"
      ? (dinnerCutoffTime || "21:15")
      : (lunchCutoffTime || "14:15");

    if (!cutoffStr) {
      setTimeLeft("--");
      return;
    }

    const [ch, cm] = cutoffStr.split(":").map(Number);

    const updateTimer = () => {
      const now = new Date();
      const cutoff = new Date();
      cutoff.setHours(ch, cm, 0, 0);

      const diffMs = cutoff.getTime() - now.getTime();
      if (diffMs <= 0) {
        setIsClosed(true);
        setTimeLeft("Closed");
      } else {
        setIsClosed(false);
        const diffSecs = Math.floor(diffMs / 1000);
        const h = Math.floor(diffSecs / 3600);
        const m = Math.floor((diffSecs % 3600) / 60);
        const s = diffSecs % 60;
        const pad = (num) => String(num).padStart(2, "0");
        setTimeLeft(`${pad(h)}:${pad(m)}:${pad(s)}`);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [category, lunchCutoffTime, dinnerCutoffTime, orderStatus]);

  if (orderStatus === "Cancelled" || orderStatus === "Completed" || orderStatus === "Delivered") {
    return <span className="text-gray-400 font-medium italic text-xs">N/A</span>;
  }

  if (isClosed) {
    return (
      <div className="inline-flex items-center gap-1 text-[11px] text-text-muted font-bold font-mono bg-gray-50 border border-gray-200/80 px-2 py-0.5 rounded-md">
        <Lock className="w-3 h-3 text-text-muted shrink-0" />
        <span>Closed</span>
      </div>
    );
  }

  if (timeLeft === "--" || !timeLeft) {
    return <span className="text-gray-400 font-medium">--</span>;
  }

  return (
    <div className="inline-flex items-center gap-1.5 font-bold font-mono text-brand-red text-xs bg-brand-red-light/10 border border-brand-red/10 px-2 py-0.5 rounded-md">
      <TickingWatch />
      <span>{timeLeft}</span>
    </div>
  );
}

export default function OrdersTable({
  orders,
  onViewDetailsClick,
  onCancelClick,
  isHistoryView = false,
  isCustomerVerified = false,
  isUpcomingTable = false,
  lunchCutoffTime = "14:15",
  dinnerCutoffTime = "18:30",
  showRowCutoff = false,
  currencySymbol = ""
}) {
  // Helper to format status badges
  const getStatusBadge = (status) => {
    switch (status) {
      case "Confirmed":
      case "Completed":
      case "Delivered":
        return "bg-success-bg text-success border-success/20";
      case "Pending":
        return "bg-warning-bg text-warning border-warning/20";
      case "Cancelled":
      case "Canceled":
        return "bg-brand-red-light text-brand-red border-brand-red/10";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  // Helper to check if row's cancellation window is closed
  const isRowCutoffPassed = (category) => {
    const cutoffStr = category?.toLowerCase() === "dinner"
      ? (dinnerCutoffTime || "21:15")
      : (lunchCutoffTime || "14:15");
    if (!cutoffStr) return true;
    const [ch, cm] = cutoffStr.split(":").map(Number);
    const now = new Date();
    const cutoff = new Date();
    cutoff.setHours(ch, cm, 0, 0);
    return now.getTime() >= cutoff.getTime();
  };

  const getPriceInRupees = (price) => price * 30;

  return (
    <div className="w-full">
      <div
        className={`w-full overflow-x-auto bg-white rounded-xl border border-border-primary shadow-xs scrollbar-thin ${isUpcomingTable ? "max-h-[420px] overflow-y-auto" : ""
          }`}
      >
        <table className="w-full text-left border-separate border-spacing-0 min-w-[700px] md:min-w-0">
          <thead>
            <tr className="bg-bg-secondary text-[11px] uppercase tracking-wider font-bold text-text-secondary sticky top-0 z-20">
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Order ID</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Meal</th>
              {isHistoryView && <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Date</th>}
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Time / Category</th>
              {isUpcomingTable && showRowCutoff && <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Cutoff Timer</th>}
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary text-center whitespace-nowrap">Qty</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Total</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary whitespace-nowrap">Status</th>
              <th className="py-4 px-6 bg-bg-secondary border-b border-border-primary text-right whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-primary text-sm bg-white">
            {orders.map((order) => {
              const isApiOrder = order.raw !== undefined;
              const priceInRupees = getPriceInRupees(order.price);
              const addons = isApiOrder ? [] : [{ name: "chhas", quantity: 3, price: 10.00 }];

              const addonTotal = addons.reduce((sum, item) => sum + (item.quantity * item.price), 0);
              const totalAmount = isApiOrder ? order.total : (priceInRupees * order.quantity) + addonTotal;

              const deliveryPref = order.delivery_preference || order.deliveryPreference || order.raw?.delivery_preference || "";
              const timeSlot = order.time_slot || order.timeSlot || order.orderTime || order.raw?.time_slot || "";

              return (
                <tr
                  key={order.id}
                  className="hover:bg-bg-secondary/40 transition-colors"
                >
                  <td className="py-4 px-6 font-mono font-semibold text-text-primary border-b border-border-primary/60 whitespace-nowrap">
                    {order.id ? (String(order.id).startsWith("#") ? order.id : `#${order.id}`) : `#${order.order_id || order.raw_order_id || "N/A"}`}
                  </td>
                  <td className="py-4 px-6 border-b border-border-primary/60">
                    <div className="flex flex-col text-left space-y-1.5 py-0.5">
                      <span className="font-bold text-text-primary block leading-snug">
                        {order.name === "Grilled Chicken Rice Bowl" ? "Kitchenfin special thali" : order.name}
                      </span>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {deliveryPref && (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-extrabold border tracking-wide uppercase transition-colors ${deliveryPref.toLowerCase().includes("pickup")
                              ? "bg-amber-100/90 text-amber-900 border-amber-300/80"
                              : deliveryPref.toLowerCase().includes("delivery")
                                ? "bg-emerald-100/90 text-emerald-900 border-emerald-300/80"
                                : "bg-brand-red-light/10 text-brand-red border-brand-red/20"
                              }`}
                          >
                            {deliveryPref.toLowerCase().includes("pickup") ? (
                              <PickupStoreIcon className="w-3 h-3 shrink-0 text-amber-900" />
                            ) : deliveryPref.toLowerCase().includes("delivery") ? (
                              <Truck className="w-2.5 h-2.5 shrink-0 text-emerald-900" />
                            ) : (
                              <Package className="w-2.5 h-2.5 shrink-0 text-brand-red" />
                            )}
                            <span>{deliveryPref}</span>
                          </span>
                        )}
                        {timeSlot && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[12px] font-semibold text-text-secondary bg-gray-100/90 border border-gray-200/80 font-mono tracking-tight">
                            <Clock className="w-3 h-3 text-text-muted shrink-0" />
                            <span>{timeSlot}</span>
                          </span>
                        )}
                      </div>
                      {addons.length > 0 && (
                        <span className="text-[10px] text-text-muted font-medium block pt-0.5">
                          Add-ons: {addons.map(a => `${a.quantity}x ${a.name}`).join(", ")}
                        </span>
                      )}
                    </div>
                  </td>
                  {isHistoryView && (
                    <td className="py-4 px-6 border-b border-border-primary/60 whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 border border-gray-200/80 text-xs font-semibold text-text-secondary font-mono tracking-tight shadow-3xs">
                        <Calendar className="w-3.5 h-3.5 text-text-muted shrink-0" />
                        <span>{order.orderDate}</span>
                      </div>
                    </td>
                  )}
                  <td className="py-4 px-6 border-b border-border-primary/60 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold capitalize border shadow-3xs ${String(order.category).toLowerCase().includes("lunch")
                        ? "bg-amber-100/80 text-amber-900 border-amber-300/80"
                        : String(order.category).toLowerCase().includes("dinner")
                          ? "bg-indigo-50/80 text-indigo-800 border-indigo-200/70"
                          : "bg-gray-50 text-gray-700 border-gray-200"
                        }`}
                    >
                      {String(order.category).toLowerCase().includes("lunch") ? (
                        <LunchSunIcon className="w-3.5 h-3.5 text-amber-800 shrink-0" />
                      ) : String(order.category).toLowerCase().includes("dinner") ? (
                        <DinnerMoonIcon className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
                      ) : (
                        <Clock className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                      )}
                      <span>{order.category}</span>
                    </span>
                  </td>
                  {isUpcomingTable && showRowCutoff && (
                    <td className="py-4 px-6 border-b border-border-primary/60">
                      <RowTimer
                        category={order.category}
                        lunchCutoffTime={lunchCutoffTime}
                        dinnerCutoffTime={dinnerCutoffTime}
                        orderStatus={order.status}
                      />
                    </td>
                  )}
                  <td className="py-4 px-6 font-mono text-center text-text-primary border-b border-border-primary/60">
                    {order.quantity}
                  </td>
                  <td className="py-4 px-6 font-bold text-text-primary font-mono border-b border-border-primary/60">
                    {order.currency_symbol || currencySymbol}{totalAmount.toFixed(2)}
                  </td>
                  <td className="py-4 px-6 border-b border-border-primary/60">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusBadge(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-right border-b border-border-primary/60">
                    <div className="flex items-center justify-end gap-2">
                      {isUpcomingTable && order.status !== "Cancelled" && order.status !== "Completed" && (
                        <div className="relative group/tooltip">
                          <button
                            onClick={() => onCancelClick(order)}
                            disabled={!isCustomerVerified || isRowCutoffPassed(order.category) || order.status === "Delivered"}
                            className="p-1.5 text-brand-red hover:text-brand-red-hover hover:bg-brand-red-light/20 rounded-lg transition-colors disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
                            title={(!isCustomerVerified || isRowCutoffPassed(order.category) || order.status === "Delivered") ? undefined : "Cancel Order"}
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                          {(!isCustomerVerified || isRowCutoffPassed(order.category) || order.status === "Delivered") && (
                            <div className="absolute right-0 bottom-full mb-2 hidden group-hover/tooltip:block w-48 bg-gray-900 text-white text-[10px] p-2 rounded-lg shadow-lg z-50 text-center leading-normal transition-all animate-fade-in">
                              {order.status === "Delivered"
                                ? "Order is already delivered and cannot be cancelled."
                                : !isCustomerVerified
                                  ? "Please verify your account to cancel this order."
                                  : "Cancellation locked: cutoff time has passed."}
                              <div className="absolute top-full right-3 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => onViewDetailsClick(order)}
                        className="p-1.5 text-brand-red hover:text-brand-red-hover hover:bg-brand-red-light/20 rounded-lg transition-colors cursor-pointer"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {isUpcomingTable && showRowCutoff && (
            <tfoot>
              <tr className="bg-brand-red-light/10 text-[10px] text-text-secondary border-t border-border-primary font-medium sticky bottom-0 z-20">
                <td colSpan={9} className="py-2.5 px-6 text-left bg-[#FFF5F5]">
                  <span className="font-bold text-brand-red">Note:</span> Cancellations are only permitted before the slot's respective cutoff time. After the cutoff, orders cannot be cancelled.
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
